import * as PIXI from "pixi.js";
import data from "../../configs/data";
import {Button} from "../../controlElements/Button";
import {CheckBox} from "../../controlElements/CheckBox";
import {RangeSlider} from "../../controlElements/RangeSlider";
import {PixiApplication} from "../../PixiApplication";
import {IAutoPlayMenu} from "./IAutoPlayMenu";

export class AutoPlayMenu extends PIXI.Container implements IAutoPlayMenu {

    private readonly SLIDER_POSITION = new PIXI.Point(-122, -135);
    private readonly LABEL_0_POSITION = new PIXI.Point(-150, -131);
    private readonly LABEL_100_POSITION = new PIXI.Point(122, -131);
    private readonly REUSE_SAME_BET_CHECKBOX_POSITION = new PIXI.Point(90, 11);
    private readonly QUICK_SPIN_CHECKBOX_POSITION = new PIXI.Point(90, 149);
    private readonly QUICK_SPIN_BUTTON_POSITION = new PIXI.Point(256, 13);

    private readonly DEFAULT_ANCHOR_POINT: any = new PIXI.Point(0.5, 0.5);
    private readonly POSSIBLE_AUTO_PLAY_VALUES: any = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    private readonly CLOSE_BUTTON_POSITION = new PIXI.Point(347, -181);

    private readonly RANGE_SLIDER_VALUE_STYLE = {
        fontSize: 32,
        fill: "#21b201",
        fontFamily: "RobotoRegular",
    };

    private readonly RANGE_SLIDER_MIN_MAX_LABEL_STYLE = {
        fontSize: 20,
        fill: "#848484",
        fontFamily: "RobotoRegular",
    };

    private readonly SPIN_BUTTON_VALUE_STYLE = {
        fontSize: 32,
        fill: "#fff",
        fontFamily: "RobotoRegular",
    };

    private reuseSameBetCheckBox: CheckBox;
    private quickSpinCheckBox: CheckBox;
    private quickSpinButton: Button;
    private autoSpinsAmountSlider: RangeSlider;
    private onClickedListeners: Array<Function> = [];

    constructor() {
        super();
        this.createAndAddBackgroundBlend();
        this.createAndAddBackground();
        this.createAndAddReuseSameBetCheckBox();
        this.createAndAddQuickSpinCheckBox();
        this.createAndAddRangeSlider();
        this.createAndAdd0Label();
        this.createAndAdd100Label();
        this.createAndAddSpinButton();
        this.createAndAddCloseButton();
        this.close();
    }

    public doReuseSameBet(): boolean {
        return this.reuseSameBetCheckBox.checked;
    }

    public open(): void {
        this.autoSpinsAmountSlider.resetToZero();
        this.quickSpinButton.enabled = false;
        this.quickSpinButton.label.text = "";
        this.visible = true;
    }

    public close(): void {
        this.visible = false;
    }

    public addOnSpinClickedListener(listener: Function): void {
        this.onClickedListeners.push(listener);
    }

    private createAndAddReuseSameBetCheckBox(): void {
        this.reuseSameBetCheckBox = new CheckBox(
            data.images.defaultCheckboxActiveOff, data.images.defaultCheckboxActiveOffHover,
            data.images.defaultCheckboxActiveOffHover, data.images.defaultCheckboxActiveOffHover,
            data.images.defaultCheckboxActiveOn, data.images.defaultCheckboxActiveOnHover,
            data.images.defaultCheckboxActiveOnHover, data.images.defaultCheckboxActiveOnHover,
            data.audios.buttonClicked);
        this.reuseSameBetCheckBox.position = this.REUSE_SAME_BET_CHECKBOX_POSITION;
        this.reuseSameBetCheckBox.checked = true;
        this.addChild(this.reuseSameBetCheckBox);
    }

    private createAndAddQuickSpinCheckBox(): void {
        this.quickSpinCheckBox = new CheckBox(
            data.images.defaultCheckboxActiveOff, data.images.defaultCheckboxActiveOffHover,
            data.images.defaultCheckboxActiveOffHover, data.images.defaultCheckboxActiveOffHover,
            data.images.defaultCheckboxActiveOn, data.images.defaultCheckboxActiveOnHover,
            data.images.defaultCheckboxActiveOnHover, data.images.defaultCheckboxActiveOnHover,
            data.audios.buttonClicked);
        this.quickSpinCheckBox.position = this.QUICK_SPIN_CHECKBOX_POSITION;
        this.addChild(this.quickSpinCheckBox);
    }

    private createAndAddRangeSlider(): void {
        this.autoSpinsAmountSlider = new RangeSlider(data.images.sliderPoint,
            data.images.sliderBackground, data.images.sliderBackgroundActive,
            0, 100);
        this.autoSpinsAmountSlider.addValuesQuantization(this.POSSIBLE_AUTO_PLAY_VALUES);
        const label = new PIXI.Text("", this.RANGE_SLIDER_VALUE_STYLE);
        label.anchor = this.DEFAULT_ANCHOR_POINT;
        label.position.y = -40;
        this.autoSpinsAmountSlider.setLabel(label);
        this.autoSpinsAmountSlider.subscribeOnValueChanged((value: number) => {
            if (value === 0) {
                this.quickSpinButton.enabled = false;
                this.quickSpinButton.label.text = "";
            } else {
                this.quickSpinButton.enabled = true;
                this.quickSpinButton.label.text = "" + value;
            }
        });
        this.autoSpinsAmountSlider.position = this.SLIDER_POSITION;
        this.addChild(this.autoSpinsAmountSlider);
    }

    private createAndAdd0Label(): void {
        const label = new PIXI.Text("0", this.RANGE_SLIDER_MIN_MAX_LABEL_STYLE);
        label.anchor = this.DEFAULT_ANCHOR_POINT;
        label.position = this.LABEL_0_POSITION;
        this.addChild(label);
    }

    private createAndAdd100Label(): void {
        const label = new PIXI.Text("100", this.RANGE_SLIDER_MIN_MAX_LABEL_STYLE);
        label.anchor = this.DEFAULT_ANCHOR_POINT;
        label.position = this.LABEL_100_POSITION;
        this.addChild(label);
    }

    private createAndAddCloseButton(): void {
        const contentClose = new Button(data.images.payTableCloseBase, data.images.payTableCloseHover,
            data.images.payTableCloseClick, data.images.payTableCloseClick, data.audios.buttonClicked);
        contentClose.position = this.CLOSE_BUTTON_POSITION;
        contentClose.subscribeOnClicked(() => {
            this.close();
        });
        this.addChild(contentClose);
    }

    private createAndAddSpinButton(): void {
        this.quickSpinButton = new Button(data.images.quickSpinButtonMenuBase,
            data.images.quickSpinButtonMenuHover, data.images.quickSpinButtonMenuInActive,
            data.images.quickSpinButtonMenuInActive, data.audios.buttonSpinClicked);
        this.quickSpinButton.position = this.QUICK_SPIN_BUTTON_POSITION;
        this.quickSpinButton.subscribeOnClicked(() => {
            this.callOnSpinClickedListeners();
            this.close();
        });
        this.quickSpinButton.label = new PIXI.Text("", this.SPIN_BUTTON_VALUE_STYLE);
        this.quickSpinButton.label.anchor = this.DEFAULT_ANCHOR_POINT;
        this.addChild(this.quickSpinButton);
    }

    private createAndAddBackground(): void {
        const background: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.autoPlayMenuBackground);
        background.anchor = this.DEFAULT_ANCHOR_POINT;
        background.interactive = true;
        this.addChild(background);
    }

    private createAndAddBackgroundBlend(): void {
        const graphics = new PIXI.Graphics();
        graphics.beginFill(0x000000, 0.3);
        graphics.drawRect(-PixiApplication.WIDTH / 2, -PixiApplication.HEIGHT / 2,
            PixiApplication.WIDTH, PixiApplication.HEIGHT);
        graphics.endFill();
        const backgroundBlend = new PIXI.Sprite(graphics.generateCanvasTexture());
        backgroundBlend.anchor = this.DEFAULT_ANCHOR_POINT;
        backgroundBlend.interactive = true;
        this.addChild(backgroundBlend);
    }

    private callOnSpinClickedListeners(): void {
        for (let i = 0; i < this.onClickedListeners.length; i++) {
            this.onClickedListeners[i](
                this.autoSpinsAmountSlider.getValue(),
                this.reuseSameBetCheckBox.checked,
                this.quickSpinCheckBox.checked,
            );
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/userInterface/autoplayMenu/AutoPlayMenu.ts